import errorMessages from 'utils/errorMessages';
import type {
  AddQueryToUrl,
  GetQueriesFromUrl,
  QueryStringFromObject,
  RemoveAllQueries,
  RemoveQuery,
  ReplaceQueryValues,
} from 'utils/types';

const queryStringFromObject: QueryStringFromObject = params => {
  try {
    const filteredParams = Object.entries(params).filter(([, value]) => {
      if (!value) {
        throw Error(errorMessages.QUERY);
      }
      return value;
    });

    return new URLSearchParams(filteredParams).toString();
  } catch (err) {
    throw new Error(err.message);
  }
};

const replaceQueryValues: ReplaceQueryValues = (url, replacement = {}) => {
  try {
    if (replacement === null || replacement === undefined) {
      throw Error(errorMessages.SECOND_ARG_NULL);
    }
    const [path] = url.split('?');
    const queries = getQueriesFromUrl(url);
    const newQueries = { ...queries, ...replacement };
    return `${path}?${queryStringFromObject(newQueries)}`;
  } catch (err) {
    throw new Error(err.message);
  }
};

const removeQuery: RemoveQuery = (url, key) => {
  try {
    const cut = url.split('?');

    if (cut.length > 2) {
      throw Error(errorMessages.QUERY);
    }

    const queries = getQueriesFromUrl(url);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [key]: removed, ...newQueries } = queries;
    const areQueriesLeft = Object.values(newQueries).length;
    return `${cut[0]}${areQueriesLeft ? `?${queryStringFromObject(newQueries)}` : ''}`;
  } catch (err) {
    throw new Error(err.message);
  }
};

const getQueriesFromUrl: GetQueriesFromUrl = url => {
  const queries = url.split('?')[1];
  if (queries) {
    const querySequence = queries.split('&');

    return querySequence.reduce((acc, el) => {
      const [key, value] = el.split('=');
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }
  return {};
};

const removeAllQueries: RemoveAllQueries = url => url.split('?')[0];

const addQueryToUrl: AddQueryToUrl = (url, qKey, qValue) => {
  const [base, params] = url.split('?');
  const searchParams = new URLSearchParams(params);
  if (qKey && qValue) {
    searchParams.append(qKey, qValue);
    return `${base}?${searchParams.toString()}`;
  }
  return base;
};

const getOffsetFromUrl = (netUlr: string) => {
  const url = new URL(netUlr);
  return url.searchParams.get('offset');
};

const setOffsetInUrl = (nextUrl: string, offset: number | string) => {
  if (!nextUrl) {
    return '';
  }
  let url = nextUrl;
  if (/^\/api\//.test(nextUrl)) {
    if (process.env.HTTPS_MODE) {
      url = `https://local.picsart.com${nextUrl}`;
    } else {
      url = `http://localhost:8080${nextUrl}`;
    }
  }
  const urlWithParams = new URL(url);
  urlWithParams.searchParams.set('offset', `${offset}`);
  const nextUrlPaths = nextUrl.split('?')[0];
  return `${nextUrlPaths}?${decodeURIComponent(urlWithParams.searchParams.toString())}`;
};

export {
  queryStringFromObject,
  replaceQueryValues,
  removeQuery,
  getQueriesFromUrl,
  removeAllQueries,
  addQueryToUrl,
  getOffsetFromUrl,
  setOffsetInUrl,
};
